// components/admin/MaterialTypeManager.js - Componente dedicato per gestione Material Types
import React, { useState, useEffect, useCallback, useRef } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { materialManager } from "../../materialManager"
import { FaGripVertical } from "react-icons/fa"

const MaterialTypeManager = ({
  isEditing,
  initialData,
  allMaterials,
  collections,
  crossCheckEnabled,
  fetchedMaterials,
  onSave,
  onCancel,
}) => {
  // === FORM STATE ===
  const [typeFormData, setTypeFormData] = useState({
    technicalName: "",
    displayLabel: "",
    display: false,
    hidden: false,
    productFilterMode: crossCheckEnabled && fetchedMaterials.length > 0,
    lang: { it: "", en: "", fr: "", de: "" },
    materials: [],
    standardMaterials: [],
    optionalMaterials: [],
  })

  // === UI STATE ===
  const [selectedCollection, setSelectedCollection] = useState("all")
  const [availableTechnicalTypes, setAvailableTechnicalTypes] = useState([])
  const formRef = useRef(null)

  // === INITIALIZATION ===
  useEffect(() => {
    if (initialData) {
      setTypeFormData({
        technicalName: initialData.technicalName || "",
        displayLabel: initialData.displayLabel || "",
        display: Boolean(initialData.display),
        hidden: Boolean(initialData.hidden),
        productFilterMode: crossCheckEnabled && fetchedMaterials.length > 0,
        lang: initialData.lang || { it: "", en: "", fr: "", de: "" },
        materials: initialData.materials || [],
        standardMaterials: initialData.standardMaterials || [],
        optionalMaterials: initialData.optionalMaterials || [],
      })
    } else {
      setTypeFormData({
        technicalName: "",
        displayLabel: "",
        display: false,
        hidden: false,
        productFilterMode: crossCheckEnabled && fetchedMaterials.length > 0,
        lang: { it: "", en: "", fr: "", de: "" },
        materials: [],
        standardMaterials: [],
        optionalMaterials: [],
      })
    }
  }, [initialData, crossCheckEnabled, fetchedMaterials])

  // Populate technical types dropdown from cross-check
  useEffect(() => {
    if (crossCheckEnabled && fetchedMaterials.length > 0) {
      const typesWithMultipleMaterials = fetchedMaterials
        .filter((type) => type.variations && type.variations.length >= 2)
        .map((type) => type.technicalName)
        .sort()

      setAvailableTechnicalTypes(typesWithMultipleMaterials)
    } else {
      setAvailableTechnicalTypes([])
    }
  }, [crossCheckEnabled, fetchedMaterials])

  // === FORM VALIDATION ===
  const isFormValid = () => {
    return typeFormData.technicalName && typeFormData.displayLabel
  }

  // === FORM HANDLERS ===
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === "checkbox" ? checked : value

    setTypeFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }))
  }

  const handleLanguageChange = (lang, value) => {
    setTypeFormData((prev) => ({
      ...prev,
      lang: {
        ...prev.lang,
        [lang]: value,
      },
    }))
  }

  // === MATERIAL MANAGEMENT ===
  const handleMaterialStatusChange = (materialName, status) => {
    setTypeFormData((prev) => {
      const newState = { ...prev }

      // Remove from both lists first
      newState.standardMaterials = newState.standardMaterials.filter(
        (m) => m !== materialName
      )
      newState.optionalMaterials = newState.optionalMaterials.filter(
        (m) => m !== materialName
      )

      // Add to appropriate list
      if (status === "standard") {
        newState.standardMaterials.push(materialName)
      } else if (status === "optional") {
        newState.optionalMaterials.push(materialName)
      }

      // Update materials list
      if (status === "standard" || status === "optional") {
        if (!newState.materials.includes(materialName)) {
          newState.materials.push(materialName)
        }
      } else {
        newState.materials = newState.materials.filter(
          (m) => m !== materialName
        )
      }

      return newState
    })
  }

  const handleRemoveMaterial = (materialName) => {
    setTypeFormData((prev) => ({
      ...prev,
      materials: prev.materials.filter((name) => name !== materialName),
      standardMaterials: prev.standardMaterials.filter(
        (name) => name !== materialName
      ),
      optionalMaterials: prev.optionalMaterials.filter(
        (name) => name !== materialName
      ),
    }))
  }

  const handleBulkAddMaterials = (
    materialsToAdd,
    asStandard = false,
    asOptional = false
  ) => {
    setTypeFormData((prev) => {
      const newMaterials = [...prev.materials]
      const newStandardMaterials = [...prev.standardMaterials]
      const newOptionalMaterials = [...prev.optionalMaterials]

      materialsToAdd.forEach((materialName) => {
        if (!newMaterials.includes(materialName)) {
          newMaterials.push(materialName)
        }

        if (asStandard && !newStandardMaterials.includes(materialName)) {
          newStandardMaterials.push(materialName)
          const optionalIndex = newOptionalMaterials.indexOf(materialName)
          if (optionalIndex > -1) {
            newOptionalMaterials.splice(optionalIndex, 1)
          }
        } else if (asOptional && !newOptionalMaterials.includes(materialName)) {
          newOptionalMaterials.push(materialName)
          const standardIndex = newStandardMaterials.indexOf(materialName)
          if (standardIndex > -1) {
            newStandardMaterials.splice(standardIndex, 1)
          }
        }
      })

      return {
        ...prev,
        materials: newMaterials,
        standardMaterials: newStandardMaterials,
        optionalMaterials: newOptionalMaterials,
      }
    })
  }

  // === DRAG AND DROP ===
  const handleMaterialsReorder = useCallback(
    (result, isStandard) => {
      if (!result.destination) return

      const materialsArray = isStandard
        ? typeFormData.standardMaterials
        : typeFormData.optionalMaterials
      const items = Array.from(materialsArray)
      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      setTypeFormData((prev) => ({
        ...prev,
        [isStandard ? "standardMaterials" : "optionalMaterials"]: items,
        materials: [
          ...prev.standardMaterials.filter((m) => m !== reorderedItem),
          ...prev.optionalMaterials.filter((m) => m !== reorderedItem),
          ...(isStandard ? items : prev.standardMaterials),
          ...(!isStandard ? items : prev.optionalMaterials),
        ].filter((v, i, a) => a.indexOf(v) === i), // Remove duplicates
      }))
    },
    [typeFormData]
  )

  // === MATERIAL FILTERING ===
  const getFilteredMaterials = () => {
    if (selectedCollection === "all") {
      return allMaterials
    }
    return allMaterials.filter(
      (material) => material.collection === selectedCollection
    )
  }

  const getFilteredMaterialsWithCrossCheck = () => {
    let filtered = getFilteredMaterials()

    if (typeFormData.productFilterMode && fetchedMaterials.length > 0) {
      const currentTechnicalName = typeFormData.technicalName
      if (currentTechnicalName) {
        const fetchedType = fetchedMaterials.find(
          (fm) =>
            fm.technicalName.toLowerCase() ===
            currentTechnicalName.toLowerCase()
        )

        if (fetchedType?.variations?.length > 0) {
          const fetchedSlugs = fetchedType.variations.map((v) => v.name.trim())
          const uniqueMaterials = new Map()

          filtered.forEach((material) => {
            const materialSlug = material.slug
              ? material.slug.trim()
              : material.name.trim()
            const matchesFetchedSlug = fetchedSlugs.some(
              (fetchedSlug) => fetchedSlug === materialSlug
            )

            if (matchesFetchedSlug) {
              const uniqueKey = materialSlug
              if (!uniqueMaterials.has(uniqueKey)) {
                uniqueMaterials.set(uniqueKey, material)
              }
            }
          })

          filtered = Array.from(uniqueMaterials.values())
        } else {
          filtered = []
        }
      }
    }

    return filtered
  }

  const getMaterialCrossCheckStatus = (materialName, technicalName = null) => {
    const typeToCheck = technicalName || typeFormData.technicalName

    if (!crossCheckEnabled || !typeToCheck || fetchedMaterials.length === 0) {
      return "normal"
    }

    const fetchedType = fetchedMaterials.find(
      (fm) => fm.technicalName.toLowerCase() === typeToCheck.toLowerCase()
    )

    if (!fetchedType?.variations?.length) {
      return "not-in-product"
    }

    const fetchedNames = fetchedType.variations.map((v) => v.name.trim())
    const materialExists = fetchedNames.some((fetchedName) => {
      if (fetchedName === materialName) return true
      if (fetchedName.trim() === materialName.trim()) return true
      return false
    })

    return materialExists ? "exists" : "not-in-product"
  }

  // === HELPER FUNCTIONS ===
  const getImageUrl = (thumbnail) => {
    if (thumbnail?.startsWith("#")) return thumbnail
    if (
      thumbnail &&
      (thumbnail.startsWith("http") || thumbnail.startsWith("//"))
    ) {
      return thumbnail
    }
    if (thumbnail) {
      const cleanPath = thumbnail.replace(/^\.\//, "")
      return `https://moroso-ayea.ensoul.worsk/${cleanPath}`
    }
    return "#"
  }

  // === FORM SUBMISSION ===
  const handleSubmit = (e) => {
    e.preventDefault()
    if (!isFormValid()) {
      alert("Technical name and display label are required")
      return
    }
    onSave(typeFormData)
  }

  // === RENDER ===
  return (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
      <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-6xl ay-max-h-screen ay-overflow-hidden ay-flex ay-flex-col">
        {/* Header fisso sempre visibile */}
        <div className="ay-flex ay-items-center ay-justify-between ay-mb-4 ay-flex-shrink-0">
          <h3 className="ay-text-lg ay-font-bold">
            {isEditing ? "Edit" : "Add"} Material Type
          </h3>

          {/* Bottoni azione sempre visibili */}
          <div className="ay-flex ay-gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => formRef.current?.requestSubmit()}
              className={`ay-px-4 ay-py-2 ay-border ay-border-transparent ay-rounded-md ay-shadow-sm ay-text-white ${
                isFormValid()
                  ? "ay-bg-blue-500 ay-hover:ay-bg-blue-600"
                  : "ay-bg-gray-400 ay-cursor-not-allowed"
              }`}
              disabled={!isFormValid()}
            >
              {isEditing ? "Update" : "Add"} Type
            </button>
          </div>
        </div>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="ay-flex ay-flex-col ay-flex-grow ay-overflow-hidden"
        >
          {/* Controlli obbligatori sempre visibili */}
          <div className="ay-flex-shrink-0 ay-mb-4 ay-pb-4 ay-border-b ay-border-gray-200">
            {/* Basic Information */}
            <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-4 ay-mb-4">
              <div>
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  Technical Name (Configurable Material in Emersya) *
                </label>
                {/* Technical Name Dropdown + Manual Input */}
                {availableTechnicalTypes.length > 0 ? (
                  <div className="ay-space-y-2">
                    <select
                      name="technicalName"
                      value={typeFormData.technicalName}
                      onChange={handleFormChange}
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    >
                      <option value="">Select from product materials...</option>
                      {availableTechnicalTypes.map((typeName) => (
                        <option key={typeName} value={typeName}>
                          {typeName} (
                          {fetchedMaterials.find(
                            (f) => f.technicalName === typeName
                          )?.variations?.length || 0}{" "}
                          materials)
                        </option>
                      ))}
                    </select>
                    <div className="ay-flex ay-items-center ay-text-xs ay-text-gray-500">
                      <span>Or type manually:</span>
                    </div>
                    <input
                      type="text"
                      name="technicalName"
                      value={typeFormData.technicalName}
                      onChange={handleFormChange}
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                      placeholder="Enter custom technical name..."
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    name="technicalName"
                    value={typeFormData.technicalName}
                    onChange={handleFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    required
                  />
                )}
                <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                  Must match the technical name used in the 3D model.
                  {crossCheckEnabled && fetchedMaterials.length > 0 && (
                    <span className="ay-text-blue-600">
                      {" "}
                      Available types are auto-detected from product.
                    </span>
                  )}
                </p>
              </div>

              <div>
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  Display Label (Fallback)
                </label>
                <input
                  type="text"
                  name="displayLabel"
                  value={typeFormData.displayLabel}
                  onChange={handleFormChange}
                  className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                />
                <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                  Default label if translations are not available.
                </p>
              </div>
            </div>

            {/* Display Settings sempre visibili */}
            <div className="ay-mb-4 ay-p-3 ay-border ay-rounded-lg ay-bg-gray-50">
              <h4 className="ay-text-sm ay-font-medium ay-mb-3">
                Display Settings
              </h4>
              <div className="ay-flex ay-gap-6">
                <label
                  className="ay-flex ay-items-center ay-gap-2"
                  title="Show only configured materials, ignore others available in viewer"
                >
                  <input
                    type="checkbox"
                    name="display"
                    checked={typeFormData.display}
                    onChange={handleFormChange}
                    className="ay-rounded"
                  />
                  <span className="ay-text-sm">
                    <strong>Display Filter</strong>
                  </span>
                </label>
                <label
                  className="ay-flex ay-items-center ay-gap-2"
                  title="Hide this material type accordion in configurator"
                >
                  <input
                    type="checkbox"
                    name="hidden"
                    checked={typeFormData.hidden}
                    onChange={handleFormChange}
                    className="ay-rounded"
                  />
                  <span className="ay-text-sm">
                    <strong>Hidden</strong>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Contenuto scrollabile */}
          <div className="ay-flex-grow ay-overflow-auto ay-pr-2">
            {/* Label Translations */}
            <div className="ay-mb-6">
              <h4 className="ay-text-md ay-font-medium ay-mb-2">
                Label Translations
              </h4>
              <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-4 ay-gap-4">
                {["it", "en", "fr", "de"].map((lang) => (
                  <div key={lang}>
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                      {lang.toUpperCase()}
                    </label>
                    <input
                      type="text"
                      value={typeFormData.lang?.[lang] || ""}
                      onChange={(e) =>
                        handleLanguageChange(lang, e.target.value)
                      }
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm"
                      placeholder={`Label in ${lang.toUpperCase()}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Material Selection */}
            <div className="ay-mb-6 ay-select-none">
              <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
                <h4 className="ay-text-md ay-font-medium ay-flex ay-items-center ay-gap-2">
                  Material Selection
                  {typeFormData.productFilterMode && (
                    <span className="ay-text-xs ay-bg-blue-100 ay-text-blue-700 ay-px-2 ay-py-1 ay-rounded">
                      Product Materials Only
                    </span>
                  )}
                </h4>
                <div className="ay-flex ay-items-center ay-gap-4">
                  {crossCheckEnabled && fetchedMaterials.length > 0 && (
                    <label className="ay-flex ay-items-center ay-gap-2 ay-text-sm ay-font-medium">
                      <input
                        type="checkbox"
                        name="productFilterMode"
                        checked={typeFormData.productFilterMode}
                        onChange={handleFormChange}
                        className="ay-rounded"
                      />
                      <span>Show only product materials</span>
                    </label>
                  )}
                  <div className="ay-flex ay-items-center ay-gap-2">
                    <label className="ay-text-sm ay-text-gray-700">
                      Filter by Collection:
                    </label>
                    <select
                      value={selectedCollection}
                      onChange={(e) => setSelectedCollection(e.target.value)}
                      disabled={typeFormData.productFilterMode}
                      className="ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500 ay-disabled:ay-bg-gray-100"
                    >
                      <option value="all">All Collections</option>
                      {collections.map((collection) => (
                        <option key={collection} value={collection}>
                          {collection}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              <div className="ay-mb-3 ay-bg-gray-50 ay-p-3 ay-rounded-md ay-flex ay-flex-wrap ay-gap-2">
                <button
                  type="button"
                  onClick={() =>
                    handleBulkAddMaterials(
                      getFilteredMaterialsWithCrossCheck().map((m) => m.name)
                    )
                  }
                  className="ay-px-3 ay-py-1 ay-bg-blue-500 ay-text-white ay-rounded ay-text-sm"
                >
                  Add All Displayed
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleBulkAddMaterials(
                      getFilteredMaterialsWithCrossCheck().map((m) => m.name),
                      true,
                      false
                    )
                  }
                  className="ay-px-3 ay-py-1 ay-bg-green-500 ay-text-white ay-rounded ay-text-sm"
                >
                  Add All as Standard
                </button>
                <button
                  type="button"
                  onClick={() =>
                    handleBulkAddMaterials(
                      getFilteredMaterialsWithCrossCheck().map((m) => m.name),
                      false,
                      true
                    )
                  }
                  className="ay-px-3 ay-py-1 ay-bg-yellow-500 ay-text-white ay-rounded ay-text-sm"
                >
                  Add All as Optional
                </button>
              </div>

              {/* Material Controls */}
              <div className="ay-flex ay-justify-between ay-mb-3 ay-bg-gray-50 ay-p-2 ay-rounded">
                <div className="ay-flex ay-items-center ay-gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      handleBulkAddMaterials(
                        getFilteredMaterialsWithCrossCheck().map((m) => m.name),
                        false,
                        true
                      )
                    }}
                    className="ay-px-2 ay-py-1 ay-bg-yellow-400 ay-text-white ay-rounded ay-text-xs"
                  >
                    Select All as Optional
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleBulkAddMaterials(
                        getFilteredMaterialsWithCrossCheck().map((m) => m.name),
                        true,
                        false
                      )
                    }}
                    className="ay-px-2 ay-py-1 ay-bg-green-500 ay-text-white ay-rounded ay-text-xs"
                  >
                    Select All as Standard
                  </button>
                </div>
                <span className="ay-text-xs ay-text-gray-500">
                  Click = Optional | Shift+Click = Standard | Click again =
                  Remove
                </span>
              </div>

              {/* Materials Grid */}
              <div className="ay-max-h-96 ay-overflow-y-auto ay-space-y-4 ay-p-2 ay-bg-gray-50 ay-rounded-md">
                {getFilteredMaterialsWithCrossCheck().length > 0 ? (
                  (() => {
                    const materialsByCollection =
                      getFilteredMaterialsWithCrossCheck().reduce(
                        (acc, material) => {
                          const collection =
                            material.collection || "Uncategorized"
                          if (!acc[collection]) acc[collection] = []
                          acc[collection].push(material)
                          return acc
                        },
                        {}
                      )
                    const sortedCollections = Object.keys(
                      materialsByCollection
                    ).sort()

                    return sortedCollections.map((collection) => (
                      <div key={collection}>
                        <h6 className="ay-font-medium ay-text-sm ay-text-gray-600 ay-mb-2 ay-px-2">
                          {collection}
                        </h6>
                        <div className="ay-grid ay-grid-cols-2 sm:ay-grid-cols-3 md:ay-grid-cols-4 lg:ay-grid-cols-8 ay-gap-3">
                          {materialsByCollection[collection]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((material, materialIndex) => {
                              const isStandard =
                                typeFormData.standardMaterials.some(
                                  (stdMat) => stdMat === material.name
                                )
                              const isOptional =
                                typeFormData.optionalMaterials.some(
                                  (optMat) => optMat === material.name
                                )
                              const crossCheckStatus =
                                getMaterialCrossCheckStatus(
                                  material.name,
                                  typeFormData.technicalName
                                )

                              let statusClass = "ay-border-gray-300"
                              if (isStandard) {
                                statusClass =
                                  "ay-border-green-500 ay-ring-2 ay-ring-green-200"
                              } else if (isOptional) {
                                statusClass =
                                  "ay-border-yellow-500 ay-ring-2 ay-ring-yellow-200"
                              }

                              let statusIndicator = null
                              if (isStandard) {
                                statusIndicator = (
                                  <div className="ay-absolute ay-top-1 ay-right-1 ay-w-6 ay-h-6 ay-bg-green-500 ay-text-white ay-rounded-full ay-flex ay-items-center ay-justify-center ay-text-xs ay-font-bold">
                                    S
                                  </div>
                                )
                              } else if (isOptional) {
                                statusIndicator = (
                                  <div className="ay-absolute ay-top-1 ay-right-1 ay-w-6 ay-h-6 ay-bg-yellow-500 ay-text-white ay-rounded-full ay-flex ay-items-center ay-justify-center ay-text-xs ay-font-bold">
                                    O
                                  </div>
                                )
                              }

                              const crossCheckClass =
                                crossCheckStatus === "not-in-product"
                                  ? "ay-opacity-50"
                                  : ""

                              return (
                                <div
                                  key={`${collection}-${
                                    material.id ||
                                    material.slug ||
                                    material.name
                                  }-${materialIndex}`}
                                  className={`ay-border ay-rounded-lg ay-p-2 ay-cursor-pointer ay-transition-[box-shadow] ay-bg-white ay-shadow-sm hover:ay-shadow-md ay-relative ${statusClass} ${crossCheckClass}`}
                                  onClick={(e) => {
                                    if (e.shiftKey) {
                                      handleMaterialStatusChange(
                                        material.name,
                                        isStandard ? "none" : "standard"
                                      )
                                    } else {
                                      handleMaterialStatusChange(
                                        material.name,
                                        isOptional ? "none" : "optional"
                                      )
                                    }
                                  }}
                                >
                                  {/* Status Indicator */}
                                  {statusIndicator}

                                  {/* Cross Check Indicator */}
                                  {crossCheckEnabled && (
                                    <div
                                      className={`ay-absolute ay-bottom-1 ay-right-1 ay-text-xs ay-px-1 ay-rounded ay-font-bold ${
                                        crossCheckStatus === "exists"
                                          ? "ay-bg-green-600 ay-text-white"
                                          : crossCheckStatus ===
                                            "not-in-product"
                                          ? "ay-bg-red-600 ay-text-white"
                                          : "ay-bg-gray-400 ay-text-white"
                                      }`}
                                      title={
                                        crossCheckStatus === "exists"
                                          ? "Exists in product"
                                          : crossCheckStatus ===
                                            "not-in-product"
                                          ? "Not in product"
                                          : "No cross-check data"
                                      }
                                    >
                                      {crossCheckStatus === "exists"
                                        ? "✓"
                                        : crossCheckStatus === "not-in-product"
                                        ? "✗"
                                        : "?"}
                                    </div>
                                  )}

                                  <div className="ay-flex ay-flex-col ay-items-center ay-gap-2">
                                    {material.thumbnail ? (
                                      material.thumbnail.startsWith("#") ? (
                                        <div
                                          className="ay-w-10 ay-h-10 ay-rounded-full ay-border"
                                          style={{
                                            backgroundColor: material.thumbnail,
                                          }}
                                        ></div>
                                      ) : (
                                        <img
                                          src={getImageUrl(material.thumbnail)}
                                          alt={material.name}
                                          className="ay-w-10 ay-h-10 ay-rounded-full ay-object-cover ay-border"
                                        />
                                      )
                                    ) : (
                                      <div className="ay-w-10 ay-h-10 ay-bg-gray-200 ay-rounded-full ay-border"></div>
                                    )}

                                    <div className="ay-text-xs ay-text-center ay-w-full">
                                      <p
                                        className="ay-truncate ay-font-medium"
                                        title={material.name}
                                      >
                                        {material.name}
                                      </p>
                                      {material.slug &&
                                        material.slug !== material.name && (
                                          <p
                                            className="ay-text-[10px] ay-text-gray-500 ay-italic ay-truncate"
                                            title={`Slug: ${material.slug}`}
                                          >
                                            {material.slug}
                                          </p>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    ))
                  })()
                ) : (
                  <div className="ay-text-center ay-py-8 ay-text-gray-500">
                    {typeFormData.productFilterMode
                      ? `No materials found in the selected product for technical type "${typeFormData.technicalName}".`
                      : "No materials found."}
                    {typeFormData.productFilterMode && (
                      <div className="ay-mt-2">
                        <button
                          type="button"
                          onClick={() =>
                            setTypeFormData((prev) => ({
                              ...prev,
                              productFilterMode: false,
                            }))
                          }
                          className="ay-text-blue-500 ay-underline ay-text-sm"
                        >
                          Show all materials instead
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Materials Summary with Drag&Drop */}
              <div className="ay-border ay-border-gray-200 ay-rounded-md ay-p-3 ay-mt-4">
                <h5 className="ay-text-sm ay-font-medium ay-mb-4">
                  Selected Materials Summary
                </h5>

                {/* Standard Materials */}
                {typeFormData.standardMaterials.length > 0 && (
                  <div className="ay-mb-4">
                    <h6 className="ay-text-xs ay-font-medium ay-text-green-700 ay-mb-2 ay-flex ay-items-center ay-gap-2">
                      <div className="ay-w-3 ay-h-3 ay-bg-green-500 ay-rounded-full"></div>
                      Standard Materials (
                      {typeFormData.standardMaterials.length})
                    </h6>

                    <DragDropContext
                      onDragEnd={(result) =>
                        handleMaterialsReorder(result, true)
                      }
                    >
                      <Droppable droppableId="standard-materials">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="ay-space-y-1"
                          >
                            {typeFormData.standardMaterials.map(
                              (materialName, index) => {
                                const materialDetails = allMaterials.find(
                                  (m) =>
                                    m.name === materialName ||
                                    m.slug === materialName
                                )

                                const crossCheckStatus =
                                  getMaterialCrossCheckStatus(
                                    materialName,
                                    typeFormData.technicalName
                                  )
                                const crossCheckIcon = crossCheckEnabled
                                  ? crossCheckStatus === "exists"
                                    ? " ✓"
                                    : crossCheckStatus === "not-in-product"
                                    ? " ✗"
                                    : " ?"
                                  : ""

                                return (
                                  <Draggable
                                    key={materialName}
                                    draggableId={`std-${materialName}`}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`ay-flex ay-items-center ay-gap-2 ay-px-2 ay-py-1 ay-bg-green-50 ay-border ay-border-green-200 ay-rounded-md ay-text-xs ${
                                          snapshot.isDragging
                                            ? "ay-shadow-lg ay-bg-green-100"
                                            : ""
                                        } ${
                                          crossCheckStatus === "not-in-product"
                                            ? "ay-opacity-60"
                                            : ""
                                        }`}
                                      >
                                        <div
                                          {...provided.dragHandleProps}
                                          className="ay-cursor-move ay-text-gray-400 ay-hover:ay-text-gray-600"
                                        >
                                          <FaGripVertical size={12} />
                                        </div>

                                        {/* Mini thumbnail */}
                                        <div className="ay-w-4 ay-h-4 ay-rounded-full ay-border ay-border-gray-300 ay-overflow-hidden ay-flex-shrink-0">
                                          {materialDetails?.thumbnail ? (
                                            materialDetails.thumbnail.startsWith(
                                              "#"
                                            ) ? (
                                              <div
                                                className="ay-w-full ay-h-full"
                                                style={{
                                                  backgroundColor:
                                                    materialDetails.thumbnail,
                                                }}
                                              />
                                            ) : (
                                              <img
                                                src={getImageUrl(
                                                  materialDetails.thumbnail
                                                )}
                                                alt={materialName}
                                                className="ay-w-full ay-h-full ay-object-cover"
                                              />
                                            )
                                          ) : (
                                            <div className="ay-w-full ay-h-full ay-bg-gray-200" />
                                          )}
                                        </div>

                                        <span className="ay-flex-grow ay-text-green-800">
                                          {materialName}
                                          {crossCheckIcon}
                                        </span>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemoveMaterial(materialName)
                                          }
                                          className="ay-text-gray-500 ay-hover:ay-text-red-600 ay-ml-1"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              }
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                )}

                {/* Optional Materials */}
                {typeFormData.optionalMaterials.length > 0 && (
                  <div className="ay-mb-4">
                    <h6 className="ay-text-xs ay-font-medium ay-text-yellow-700 ay-mb-2 ay-flex ay-items-center ay-gap-2">
                      <div className="ay-w-3 ay-h-3 ay-bg-yellow-500 ay-rounded-full"></div>
                      Optional Materials (
                      {typeFormData.optionalMaterials.length})
                    </h6>

                    <DragDropContext
                      onDragEnd={(result) =>
                        handleMaterialsReorder(result, false)
                      }
                    >
                      <Droppable droppableId="optional-materials">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="ay-space-y-1"
                          >
                            {typeFormData.optionalMaterials.map(
                              (materialName, index) => {
                                const materialDetails = allMaterials.find(
                                  (m) =>
                                    m.name === materialName ||
                                    m.slug === materialName
                                )

                                const crossCheckStatus =
                                  getMaterialCrossCheckStatus(
                                    materialName,
                                    typeFormData.technicalName
                                  )
                                const crossCheckIcon = crossCheckEnabled
                                  ? crossCheckStatus === "exists"
                                    ? " ✓"
                                    : crossCheckStatus === "not-in-product"
                                    ? " ✗"
                                    : " ?"
                                  : ""

                                return (
                                  <Draggable
                                    key={materialName}
                                    draggableId={`opt-${materialName}`}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`ay-flex ay-items-center ay-gap-2 ay-px-2 ay-py-1 ay-bg-yellow-50 ay-border ay-border-yellow-200 ay-rounded-md ay-text-xs ${
                                          snapshot.isDragging
                                            ? "ay-shadow-lg ay-bg-yellow-100"
                                            : ""
                                        } ${
                                          crossCheckStatus === "not-in-product"
                                            ? "ay-opacity-60"
                                            : ""
                                        }`}
                                      >
                                        <div
                                          {...provided.dragHandleProps}
                                          className="ay-cursor-move ay-text-gray-400 ay-hover:ay-text-gray-600"
                                        >
                                          <FaGripVertical size={12} />
                                        </div>

                                        {/* Mini thumbnail */}
                                        <div className="ay-w-4 ay-h-4 ay-rounded-full ay-border ay-border-gray-300 ay-overflow-hidden ay-flex-shrink-0">
                                          {materialDetails?.thumbnail ? (
                                            materialDetails.thumbnail.startsWith(
                                              "#"
                                            ) ? (
                                              <div
                                                className="ay-w-full ay-h-full"
                                                style={{
                                                  backgroundColor:
                                                    materialDetails.thumbnail,
                                                }}
                                              />
                                            ) : (
                                              <img
                                                src={getImageUrl(
                                                  materialDetails.thumbnail
                                                )}
                                                alt={materialName}
                                                className="ay-w-full ay-h-full ay-object-cover"
                                              />
                                            )
                                          ) : (
                                            <div className="ay-w-full ay-h-full ay-bg-gray-200" />
                                          )}
                                        </div>

                                        <span className="ay-flex-grow ay-text-yellow-800">
                                          {materialName}
                                          {crossCheckIcon}
                                        </span>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemoveMaterial(materialName)
                                          }
                                          className="ay-text-gray-500 ay-hover:ay-text-red-600 ay-ml-1"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              }
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                )}

                {/* Empty state */}
                {typeFormData.materials.length === 0 && (
                  <div className="ay-text-center ay-py-4">
                    <p className="ay-text-sm ay-text-gray-500">
                      No materials selected yet.
                    </p>
                    <p className="ay-text-xs ay-text-gray-400 ay-mt-1">
                      Select materials from the grid above to see them organized
                      here.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MaterialTypeManager
